import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { DebounceInput } from 'react-debounce-input';
import Popup from 'reactjs-popup';
import {
  Breadcrumbs, Tabs, Avatar, Badge, Button, EmptyState, Table, Tag, Edit01, Mail01, Trash01,
} from '@opendate-app/setlist';
import MailkickSubscriptionIndexContainer from '../../Marketing/MailkickSubscriptions/MailkickSubscriptionIndexContainer';
import CrmDualModeTabs from '../../shared/components/CrmDualModeTabs';
import TeamPagination from '../../TeamManager/components/TeamPagination';
import DonationsTab from '../../shared/components/DonationsTab';
import { deriveNoteTitle } from '../../shared/components/CrmNotesCard';
import CrmPageLayout from '../../shared/components/CrmPageLayout';
import CrmCustomFieldsCard from '../../shared/components/CrmCustomFieldsCard';
import ContactFormModal from '../../Contacts/components/ContactFormModal';
import { extractDomain, isFreeEmailDomain } from '../../shared/domainUtils';
import useTransactions from '../hooks/useTransactions';
import useHouseholds from '../hooks/useHouseholds';
import useMemberships from '../hooks/useMemberships';
import useDonations from '../hooks/useDonations';
import useSoftCredits from '../hooks/useSoftCredits';
import LogDonationModal from './LogDonationModal';
import SoftCreditsModal from '../../shared/components/SoftCreditsModal';
import FairMarketValueModal from '../../shared/components/FairMarketValueModal';
import SoftCreditsReceivedCard from '../../shared/components/SoftCreditsReceivedCard';
import MapWithMarker from '../../MapWithMarker/components/MapWithMarker';
import TransactionsTab from './tabs/TransactionsTab';
import ActivityTab from './tabs/ActivityTab';
import HouseholdsTab from './tabs/HouseholdsTab';
import MembershipsTab from './tabs/MembershipsTab';
import PledgesTab from '../../shared/components/pledges/PledgesTab';
import ContactStatTiles from './overview/ContactStatTiles';
import UpcomingEventsCard from './overview/UpcomingEventsCard';
import GivingCard from './overview/GivingCard';
import AtTheShowsCard from './overview/AtTheShowsCard';
import MusicTasteCard from './overview/MusicTasteCard';
import ActivityFeed from './overview/ActivityFeedCard';
import useActivityFeed from '../hooks/useActivityFeed';
import ContactInfoCard from './sidebar/ContactInfoCard';
import AffiliationsCard from './sidebar/AffiliationsCard';
import IntroducedByCard from './sidebar/IntroducedByCard';
import SocialCard from './sidebar/SocialCard';
import AssignedStaffRow from './sidebar/AssignedStaffRow';
import VenuePurchasesCard from './sidebar/VenuePurchasesCard';
import SubscriptionsCard from './sidebar/SubscriptionsCard';
import TagsCard from './sidebar/TagsCard';
import FilesCard from './sidebar/FilesCard';
import crmLabels from '../../shared/utils/crmLabels';

const axios = require('axios').default;

function ContactDetailsPage({ team, contact, csrfToken, contactTypes = [], customFieldDefinitions = [], currentUser, fanPermissions, canLogDonations = false, relationshipOptions = [], donationCustomFieldDefinitions = [], staffOptions = null, assignedStaff = null, canAssignStaff = false, introducedBy = null, introducedContacts, attributionTimeline = null }) {
  const labels = crmLabels(team);
  const orgSingularLower = labels.organization.singular.toLowerCase();
  const orgPluralLower = labels.organization.plural.toLowerCase();
  if (!team || !contact) return null;

  const [currentContact, setCurrentContact] = useState(contact);

  const [showAllSegments, setShowAllSegments] = useState(false);
  const tabItems = [
    { value: 'overview', label: 'Overview' },
    ...(attributionTimeline ? [{ value: 'activity', label: 'Activity' }] : []),
    { value: 'transactions', label: 'Transactions' },
    { value: 'donations', label: 'Donations' },
    { value: 'pledges', label: 'Pledges' },
    { value: 'organizations', label: labels.organization.plural },
    { value: 'households', label: 'Households' },
    { value: 'memberships', label: 'Memberships' },
    ...(team.volunteers_enabled !== false ? [{ value: 'volunteers', label: 'Volunteers' }] : []),
    { value: 'permissions', label: 'Permissions' },
  ];
  const tabFromHash = () => {
    const hash = typeof window !== 'undefined' ? window.location.hash.slice(1) : '';
    return tabItems.some((item) => item.value === hash) ? hash : 'overview';
  };
  const [activeTab, setActiveTab] = useState(tabFromHash);
  const selectTab = (key) => {
    setActiveTab(key);
    window.location.hash = key;
  };

  useEffect(() => {
    const onHashChange = () => setActiveTab(tabFromHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);
  const [avatarHovered, setAvatarHovered] = useState(false);
  const avatarFileInputRef = useRef(null);

  const [notes, setNotes] = useState([]);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [deleteNoteSubmitting, setDeleteNoteSubmitting] = useState(false);

  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [attachmentsError, setAttachmentsError] = useState('');

  const transactionsHook = useTransactions({
    team,
    contact: currentContact,
    csrfToken,
    enabled: activeTab === 'transactions',
  });

  const householdsHook = useHouseholds({
    team,
    contact: currentContact,
    csrfToken,
  });

  const membershipsHook = useMemberships({
    team,
    contact: currentContact,
    csrfToken,
    enabled: activeTab === 'memberships',
  });

  const donationsHook = useDonations({
    team,
    contact: currentContact,
    csrfToken,
    enabled: activeTab === 'donations',
  });

  const softCreditsEnabled = team.soft_credits_enabled !== false;
  const softCreditsHook = useSoftCredits({
    team,
    contact: currentContact,
    csrfToken,
    enabled: activeTab === 'donations' && softCreditsEnabled,
  });

  const activityFeedHook = useActivityFeed({
    team,
    contact: currentContact,
    csrfToken,
    notes,
    attachments,
    enabled: activeTab === 'overview',
  });

  const [showLogDonationModal, setShowLogDonationModal] = useState(false);
  // The donation row whose "Add/Edit Soft Credits" modal is open (null = closed).
  const [softCreditsModalDonation, setSoftCreditsModalDonation] = useState(null);
  const [fairMarketValueDonation, setFairMarketValueDonation] = useState(null);

  const [companyNames, setCompanyNames] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [domainOnlyOrgIds, setDomainOnlyOrgIds] = useState(new Set());
  const [showOrganizationModal, setShowOrganizationModal] = useState(false);
  const [organizationModalTab, setOrganizationModalTab] = useState('existing');

  const [addOrgName, setAddOrgName] = useState('');
  const [addOrgCity, setAddOrgCity] = useState('');
  const [addOrgState, setAddOrgState] = useState('');
  const [addOrgZip, setAddOrgZip] = useState('');
  const [addOrgCountry, setAddOrgCountry] = useState('');
  const [addOrgEmail, setAddOrgEmail] = useState('');
  const [addOrgPhone, setAddOrgPhone] = useState('');
  const [addOrgWebsiteUrl, setAddOrgWebsiteUrl] = useState('');
  const [addOrgFacebookUrl, setAddOrgFacebookUrl] = useState('');
  const [addOrgInstagramUrl, setAddOrgInstagramUrl] = useState('');
  const [addOrgTwitterUrl, setAddOrgTwitterUrl] = useState('');
  const [addOrgLinkedinUrl, setAddOrgLinkedinUrl] = useState('');
  const [addOrgSubmitting, setAddOrgSubmitting] = useState(false);
  const [addOrgErrors, setAddOrgErrors] = useState([]);

  const [attachOrgSearch, setAttachOrgSearch] = useState('');
  const [attachOrgResults, setAttachOrgResults] = useState([]);
  const [attachOrgSelectedId, setAttachOrgSelectedId] = useState(null);
  const [attachOrgLoading, setAttachOrgLoading] = useState(false);
  const [attachOrgSubmitting, setAttachOrgSubmitting] = useState(false);
  const [attachOrgError, setAttachOrgError] = useState('');
 
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editFacebookUrl, setEditFacebookUrl] = useState('');
  const [editInstagramUrl, setEditInstagramUrl] = useState('');
  const [editTwitterUrl, setEditTwitterUrl] = useState('');
  const [editLinkedinUrl, setEditLinkedinUrl] = useState('');
  const [editingSocialField, setEditingSocialField] = useState(null);
  const [socialSubmitting, setSocialSubmitting] = useState(false);
  const [socialError, setSocialError] = useState('');
  const [hoverSocialField, setHoverSocialField] = useState(null);

  const [editingNote, setEditingNote] = useState(null);
  const [noteTitle, setNoteTitle] = useState('');
  const [noteBody, setNoteBody] = useState('');
  const [noteSubmitting, setNoteSubmitting] = useState(false);
  const [noteErrors, setNoteErrors] = useState([]);

  const [showTagPopup, setShowTagPopup] = useState(false);
  const [taggedItems, setTaggedItems] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [tagSearchValue, setTagSearchValue] = useState('');

  const fullName = [currentContact.first_name, currentContact.last_name]
    .filter(Boolean)
    .join(' ') || '(No name)';
  const email = currentContact.email || '';
  const isMember = !!currentContact.is_member;
  const hasGiving = !!(currentContact.giving && currentContact.giving.count > 0);
  const facebookUrl = currentContact.facebook_url || '';
  const instagramUrl = currentContact.instagram_url || '';
  const twitterUrl = currentContact.twitter_url || '';
  const linkedinUrl = currentContact.linkedin_url || '';

  // After the soft-credits modal changes anything, refresh the donations list
  // (row summaries) and the soft-credit totals.
  const handleSoftCreditsChanged = () => {
    donationsHook.reload();
    softCreditsHook.reload();
  };


  useEffect(() => {
    if (!team || !team.id || !csrfToken) return;

    const fetchPageData = async () => {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

      const contactDomain = extractDomain(contact.email);
      const shouldFetchDomainOrgs = contactDomain && !isFreeEmailDomain(contactDomain);

      try {
        const [filterValuesRes, companyOrgsRes, domainByWebsiteRes, domainByEmailRes] = await Promise.all([
          axios.get(`/teams/${team.id}/crm/fans/filter_values`),
          contact.company_name
            ? axios.get(`/teams/${team.id}/crm/organizations.json`, {
                params: { 'q[name_eq]': contact.company_name, per_page: 100 },
              })
            : Promise.resolve(null),
          shouldFetchDomainOrgs
            ? axios.get(`/teams/${team.id}/crm/organizations.json`, {
                params: { 'q[website_url_cont]': contactDomain, per_page: 100 },
              })
            : Promise.resolve(null),
          shouldFetchDomainOrgs
            ? axios.get(`/teams/${team.id}/crm/organizations.json`, {
                params: { 'q[email_cont]': `@${contactDomain}`, per_page: 100 },
              })
            : Promise.resolve(null),
        ]);

        const filterValues = filterValuesRes.data || {};
        setCompanyNames((filterValues.company_names || []).map((c) => c.value));

        const companyOrgs = companyOrgsRes ? (companyOrgsRes.data.organizations || []) : [];
        const domainOrgsMap = new Map();
        (domainByWebsiteRes ? (domainByWebsiteRes.data.organizations || []) : [])
          .forEach((o) => domainOrgsMap.set(o.id, o));
        (domainByEmailRes ? (domainByEmailRes.data.organizations || []) : [])
          .forEach((o) => { if (!domainOrgsMap.has(o.id)) domainOrgsMap.set(o.id, o); });
        const domainOrgs = Array.from(domainOrgsMap.values());

        const companyOrgIds = new Set(companyOrgs.map((o) => o.id));
        const merged = new Map();
        companyOrgs.forEach((o) => merged.set(o.id, o));
        domainOrgs.forEach((o) => { if (!merged.has(o.id)) merged.set(o.id, o); });

        const domainOnly = new Set();
        domainOrgs.forEach((o) => { if (!companyOrgIds.has(o.id)) domainOnly.add(o.id); });

        setOrganizations(Array.from(merged.values()));
        setDomainOnlyOrgIds(domainOnly);
      } catch (e) {
        toast.error('Failed to load contact details.');
      }
    };

    fetchPageData();
  }, [team && team.id, csrfToken, contact.company_name, contact.email]);

  useEffect(() => {
    if (!team || !currentContact || !currentContact.id) return;

    const fetchTags = async () => {
      if (!csrfToken) return;

      try {
        axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
        const [itemsResponse, tagsResponse] = await Promise.all([
          axios.get(`/teams/${team.id}/tagged_items`, {
            params: {
              taggable_type: 'Fan',
              taggable_id: currentContact.id,
            },
          }),
          axios.get(`/teams/${team.id}/crm/tags`, {
            params: {
              scope: 'Fan',
            },
          }),
        ]);

        setTaggedItems(itemsResponse.data || []);
        setAvailableTags(tagsResponse.data || []);
      } catch (err) {
        toast.error('Failed to load tags.');
      }
    };

    fetchTags();
  }, [team, currentContact, csrfToken]);

  useEffect(() => {
    if (!team || !currentContact || !currentContact.id) return;

    const fetchNotes = async () => {
      try {
        const response = await axios.get(
          `/teams/${team.id}/crm/notes.json?notable_type=Fan&notable_id=${currentContact.id}`,
        );
        setNotes(response.data || []);
      } catch (err) {
        toast.error('Failed to load notes.');
      }
    };

    fetchNotes();
  }, [team, currentContact]);

  const loadAttachments = async () => {
    if (!team || !team.id || !currentContact || !currentContact.id) return;
    try {
      setAttachmentsLoading(true);
      setAttachmentsError('');
      const response = await axios.get(
        `/teams/${team.id}/crm/fans/${currentContact.id}/attachments.json`,
      );
      setAttachments(response.data || []);
    } catch (err) {
      setAttachmentsError('Failed to load attachments');
    } finally {
      setAttachmentsLoading(false);
    }
  };

  useEffect(() => {
    loadAttachments();
  }, [team, currentContact]);

  const handleUploadAttachment = async (file) => {
    if (!file || !team || !team.id || !currentContact || !currentContact.id || !csrfToken) return;

    try {
      setAttachmentsError('');
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(
        `/teams/${team.id}/crm/fans/${currentContact.id}/attachments.json`,
        formData,
      );
      setAttachments((prev) => [response.data, ...prev]);
    } catch (err) {
      const messages = err && err.response && err.response.data;
      const message = Array.isArray(messages) ? messages.join(', ') : 'Failed to upload attachment';
      setAttachmentsError(message);
      toast.error(message);
    }
  };

  const handleDeleteAttachment = async (attachment) => {
    if (!attachment || !team || !team.id || !currentContact || !currentContact.id || !csrfToken) return;

    try {
      setAttachmentsError('');
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      await axios.delete(
        `/teams/${team.id}/crm/fans/${currentContact.id}/attachments/${attachment.id}.json`,
      );
      setAttachments((prev) => prev.filter((a) => a.id !== attachment.id));
    } catch (err) {
      setAttachmentsError('Failed to delete attachment');
      toast.error('Failed to delete attachment');
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showTagPopup && !event.target.closest('.cdp-tag-popup-container')) {
        setShowTagPopup(false);
      }
    };

    if (showTagPopup) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTagPopup]);

  const openEditModalForContact = () => setFormModalOpen(true);

  const handleContactSaved = (saved) => {
    setCurrentContact((prev) => ({ ...prev, ...saved }));
  };

  const reloadTags = async () => {
    const [itemsResponse, tagsResponse] = await Promise.all([
      axios.get(`/teams/${team.id}/tagged_items`, {
        params: { taggable_type: 'Fan', taggable_id: currentContact.id },
      }),
      axios.get(`/teams/${team.id}/crm/tags`, { params: { scope: 'Fan' } }),
    ]);

    setTaggedItems(itemsResponse.data || []);
    setAvailableTags(tagsResponse.data || []);
  };

  const handleToggleTag = async (tag, isActive, taggedItem) => {
    if (!team || !currentContact || !csrfToken) return;

    try {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

      if (isActive && taggedItem) {
        await axios.delete(`/teams/${team.id}/tagged_items/${taggedItem.id}`);
      } else {
        await axios.post(`/teams/${team.id}/tagged_items`, {
          tagged_item: {
            taggable_id: currentContact.id,
            taggable_type: 'Fan',
            tag_id: tag.id,
          },
        });
      }

      await reloadTags();
    } catch (err) {
      toast.error(isActive ? 'Failed to remove tag.' : 'Failed to add tag.');
    }
  };

  const handleCreateAndAddTag = async (tagName) => {
    if (!team || !currentContact || !csrfToken || !tagName.trim()) return;

    try {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

      await axios.post(`/teams/${team.id}/tagged_items`, {
        tagged_item: {
          taggable_id: currentContact.id,
          taggable_type: 'Fan',
          tag_attributes: { name: tagName },
        },
      });

      await reloadTags();
      setTagSearchValue('');
    } catch (err) {
      toast.error('Failed to create tag.');
    }
  };

  const handleCreateNote = async (body) => {
    if (!team || !team.id || !currentContact || !currentContact.id || !csrfToken) {
      throw new Error('Missing context to create note');
    }
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    const { data } = await axios.post(`/teams/${team.id}/crm/notes.json`, {
      note: {
        title: deriveNoteTitle(body),
        body,
        notable_type: 'Fan',
        notable_id: currentContact.id,
      },
    });
    setNotes((prev) => [data, ...prev]);
  };

  const openEditNoteModal = (note) => {
    setEditingNote(note || null);
    setNoteTitle(note?.title || '');
    setNoteBody(note?.body || '');
    setNoteErrors([]);
    setNoteSubmitting(false);
  };

  const closeNoteModal = () => {
    setEditingNote(null);
    setNoteSubmitting(false);
    setNoteErrors([]);
  };

  const canSubmitNoteForm = noteBody && noteBody.trim().length > 0;

  const handleSaveNote = async (e) => {
    e.preventDefault();
    if (!canSubmitNoteForm) return;
    if (!team || !team.id || !currentContact || !currentContact.id || !csrfToken) return;

    try {
      setNoteSubmitting(true);
      setNoteErrors([]);
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

      const payload = {
        note: {
          title: noteTitle,
          body: noteBody,
          notable_type: 'Fan',
          notable_id: currentContact.id,
        },
      };

      let response;
      if (editingNote && editingNote.id) {
        response = await axios.patch(
          `/teams/${team.id}/crm/notes/${editingNote.id}.json`,
          payload,
        );
        const updated = response.data;
        setNotes((prev) => prev.map((n) => (n.id === updated.id ? updated : n)));
      } else {
        response = await axios.post(
          `/teams/${team.id}/crm/notes.json`,
          payload,
        );
        const created = response.data;
        setNotes((prev) => [created, ...prev]);
      }

      closeNoteModal();
    } catch (err) {
      const resp = err.response;
      if (resp && Array.isArray(resp.data)) {
        setNoteErrors(resp.data);
      } else {
        setNoteErrors(['Failed to save note']);
      }
    } finally {
      setNoteSubmitting(false);
    }
  };

  const handleDeleteNote = (note) => {
    if (!note || !note.id) return;
    setNoteToDelete(note);
  };

  const closeDeleteNoteModal = () => {
    setNoteToDelete(null);
    setDeleteNoteSubmitting(false);
  };

  const confirmDeleteNote = async () => {
    if (!team || !team.id || !currentContact || !currentContact.id || !csrfToken) return;
    if (!noteToDelete || !noteToDelete.id) return;

    setDeleteNoteSubmitting(true);
    try {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      await axios.delete(
        `/teams/${team.id}/crm/notes/${noteToDelete.id}.json`,
      );
      setNotes((prev) => prev.filter((n) => n.id !== noteToDelete.id));
      closeDeleteNoteModal();
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert('Failed to delete note');
      setDeleteNoteSubmitting(false);
    }
  };

  const startEditingSocial = (field) => {
    setEditingSocialField(field);
    setSocialError('');

    if (field === 'facebook_url') setEditFacebookUrl(facebookUrl);
    if (field === 'instagram_url') setEditInstagramUrl(instagramUrl);
    if (field === 'twitter_url') setEditTwitterUrl(twitterUrl);
    if (field === 'linkedin_url') setEditLinkedinUrl(linkedinUrl);
  };

  const handleSaveSocial = async () => {
    if (!editingSocialField || !team || !team.id || !currentContact || !currentContact.id || !csrfToken) {
      setEditingSocialField(null);
      return;
    }

    let value = '';
    if (editingSocialField === 'facebook_url') value = editFacebookUrl;
    if (editingSocialField === 'instagram_url') value = editInstagramUrl;
    if (editingSocialField === 'twitter_url') value = editTwitterUrl;
    if (editingSocialField === 'linkedin_url') value = editLinkedinUrl;

    try {
      setSocialSubmitting(true);
      setSocialError('');
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

      const payload = {
        fan: {
          [editingSocialField]: value || null,
        },
      };

      const response = await axios.patch(
        `/teams/${team.id}/crm/fans/${currentContact.id}.json`,
        payload,
      );

      const saved = response.data;
      setCurrentContact((prev) => ({ ...prev, ...saved }));
      setEditingSocialField(null);
    } catch (err) {
      setSocialError('Incomplete URL');
    } finally {
      setSocialSubmitting(false);
    }
  };

  const handleAvatarUpload = async (file) => {
    if (!team || !team.id || !currentContact || !currentContact.id || !csrfToken) return;

    try {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      const formData = new FormData();
      formData.append('fan[avatar]', file);

      const response = await axios.patch(
        `/teams/${team.id}/crm/fans/${currentContact.id}.json`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );

      const saved = response.data;
      setCurrentContact((prev) => ({ ...prev, ...saved }));
      toast.success('Avatar updated');
    } catch (err) {
      toast.error('Failed to update avatar');
    }
  };

  const handleAvatarFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleAvatarUpload(file);
    }
    e.target.value = '';
  };

  const openOrganizationModal = () => {
    setOrganizationModalTab('existing');

    setAddOrgName('');
    setAddOrgCity('');
    setAddOrgState('');
    setAddOrgZip('');
    setAddOrgCountry('');
    setAddOrgEmail('');
    setAddOrgPhone('');
    setAddOrgWebsiteUrl('');
    setAddOrgFacebookUrl('');
    setAddOrgInstagramUrl('');
    setAddOrgTwitterUrl('');
    setAddOrgLinkedinUrl('');
    setAddOrgErrors([]);
    setAddOrgSubmitting(false);

    setAttachOrgSearch('');
    setAttachOrgResults([]);
    setAttachOrgSelectedId(null);
    setAttachOrgError('');
    setAttachOrgLoading(false);
    setAttachOrgSubmitting(false);

    setShowOrganizationModal(true);
  };

  const canSubmitCreateOrgForm = addOrgName && addOrgName.trim().length > 0;

  const handleDetachOrganization = async (event, organization) => {
    event.stopPropagation();
    if (!team || !team.id || !csrfToken || !currentContact || !currentContact.id) return;

    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(
      `Remove this ${orgSingularLower} from this contact? The ${orgSingularLower} will remain in your ${orgPluralLower} list.`,
    );
    if (!confirmed) return;

    try {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      const response = await axios.patch(
        `/teams/${team.id}/crm/fans/${currentContact.id}.json`,
        {
          fan: {
            company_name: null,
          },
        },
      );
      const updatedContact = response.data;
      setCurrentContact((prev) => ({ ...prev, ...updatedContact }));
      setOrganizations([]);
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(`Failed to detach ${orgSingularLower}`);
    }
  };

  return (
    <CrmPageLayout team={team} activeSidebarKey="contacts">
          <div className="card mb-3 cdp-main-card">
            <div className="card-body">
              <div style={{ marginBottom: 12 }}>
                <Breadcrumbs items={[
                  { label: labels.contact.plural, href: `/teams/${team.id}/crm/fans` },
                  { label: `${labels.contact.singular} Details` },
                ]} />
              </div>

              <div className="d-flex flex-wrap justify-content-between align-items-start" style={{ gap: 16 }}>
                <div className="d-flex align-items-center" style={{ gap: 16, minWidth: 0, flex: '1 1 280px' }}>
                  <div
                    style={{ position: 'relative', width: 56, height: 56, flexShrink: 0 }}
                    onMouseEnter={() => setAvatarHovered(true)}
                    onMouseLeave={() => setAvatarHovered(false)}
                  >
                    <Avatar
                      initials={[currentContact.first_name, currentContact.last_name].filter(Boolean).map((w) => w[0]).join('').toUpperCase() || '?'}
                      src={currentContact.avatar_thumb_url || undefined}
                      alt={fullName}
                      size="lg"
                    />
                    <input
                      ref={avatarFileInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handleAvatarFileChange}
                    />
                    <button
                      type="button"
                      aria-label="Change avatar"
                      onClick={() => avatarFileInputRef.current && avatarFileInputRef.current.click()}
                      onFocus={() => setAvatarHovered(true)}
                      onBlur={() => setAvatarHovered(false)}
                      style={{
                        position: 'absolute',
                        inset: 0,
                        border: 'none',
                        padding: 0,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: avatarHovered ? 1 : 0,
                        transition: 'opacity 0.15s ease-in-out',
                        cursor: 'pointer',
                      }}
                    >
                      <i className="fal fa-camera" style={{ color: 'var(--sl-text-white)', fontSize: 20 }} aria-hidden="true" />
                    </button>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div className="d-flex align-items-center" style={{ gap: 12, flexWrap: 'wrap' }}>
                      <h1 className="mb-0" style={{ fontSize: 24, fontWeight: 600, color: 'var(--sl-text-primary)' }}>{fullName}</h1>
                      {currentContact.active_donor && <Badge color="success" dot>Active donor</Badge>}
                      {isMember && <Badge color="blue">Member</Badge>}
                      {(currentContact.contact_types || []).map((type) => (
                        <Badge key={`contact-type-${type.id}`} color="gray">{type.archived ? `${type.name} (archived)` : type.name}</Badge>
                      ))}
                    </div>
                    {email && <div className="text-break" style={{ color: 'var(--sl-text-secondary)', fontSize: 14 }}>{email}</div>}
                  </div>
                </div>
                <div className="d-flex align-items-center flex-shrink-0" style={{ gap: 10 }}>
                  {email && <Button variant="secondary" icon={Mail01} onClick={() => { window.location.href = `mailto:${email}`; }}>Email</Button>}
                  <Button variant="primary" icon={Edit01} onClick={openEditModalForContact}>{`Edit ${labels.contact.singular}`}</Button>
                </div>
              </div>

              <div style={{ height: 20 }} />

              <Tabs
                aria-label="Contact detail sections"
                collapseBelow={0}
                overflow
                value={activeTab}
                onChange={selectTab}
                items={tabItems}
              />

              {activeTab === 'overview' && (
                <>
                  <hr style={{ margin: '0 0 20px 0', border: 'none', borderTop: '1px solid var(--sl-border-secondary)' }} />
                  <div className="row">
                    <div className="col-lg-8">
                      <div className="d-flex flex-column" style={{ gap: 20 }}>
                        <ContactStatTiles
                          totalEvents={currentContact.total_events}
                          lifetimeSpend={currentContact.lifetime_spend}
                          averageTicket={currentContact.average_ticket}
                        />
                        <UpcomingEventsCard team={team} events={currentContact.upcoming_events || []} />
                        {hasGiving ? (
                          <div className="cdp-two-up">
                            <GivingCard giving={currentContact.giving} />
                            <AtTheShowsCard
                              totalEvents={currentContact.total_events}
                              ticketsCount={currentContact.tickets_count}
                              lifetimeSpend={currentContact.lifetime_spend}
                              showsByYear={currentContact.shows_by_year || []}
                            />
                          </div>
                        ) : (
                          <AtTheShowsCard
                            totalEvents={currentContact.total_events}
                            ticketsCount={currentContact.tickets_count}
                            lifetimeSpend={currentContact.lifetime_spend}
                            showsByYear={currentContact.shows_by_year || []}
                          />
                        )}
                        <MusicTasteCard
                          rankedArtists={currentContact.ranked_artists || []}
                          rankedGenres={currentContact.ranked_genres || []}
                        />
                        <div className="cdp-card">
                          <h2 className="cdp-card-title">Segments</h2>
                          <div className="cdp-pill-row">
                            {(currentContact.segments || []).length === 0 ? (
                              <span className="cdp-field-value">--</span>
                            ) : (
                              <>
                                {(showAllSegments ? currentContact.segments : currentContact.segments.slice(0, 5)).map((segment) => (
                                  <a key={segment.id} href={`/teams/${team.id}/segments/${segment.id}`} className="cdp-pill-link">
                                    <Tag>{segment.name}</Tag>
                                  </a>
                                ))}
                                {currentContact.segments.length > 5 && (
                                  <button
                                    type="button"
                                    onClick={() => setShowAllSegments((prev) => !prev)}
                                    className="btn btn-link p-0 cdp-field-value"
                                    style={{ fontSize: 12, textDecoration: 'none' }}
                                  >
                                    {showAllSegments ? 'Show less' : `+${currentContact.segments.length - 5} more`}
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        <CrmCustomFieldsCard
                          teamId={team.id}
                          customFieldDefinitions={customFieldDefinitions}
                          customFieldValues={currentContact.custom_field_values}
                        />
                        <div className="cdp-overview-activity-spacer">
                          <ActivityFeed
                            feed={activityFeedHook.feed}
                            counts={activityFeedHook.counts}
                            loading={activityFeedHook.loading}
                            onCreateNote={handleCreateNote}
                            onEditNote={openEditNoteModal}
                            onDeleteNote={handleDeleteNote}
                            onUploadAttachment={handleUploadAttachment}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-4 cdp-overview-sidebar">
                      <ContactInfoCard contact={currentContact} onAdd={openEditModalForContact}>
                        {staffOptions && (
                          <AssignedStaffRow
                            teamId={team.id}
                            contactId={currentContact.id}
                            csrfToken={csrfToken}
                            staffOptions={staffOptions}
                            assignedStaffId={currentContact.assigned_staff_id}
                            assignedStaff={assignedStaff}
                            canEdit={canAssignStaff}
                            onSaved={(saved) => setCurrentContact((prev) => ({ ...prev, ...saved }))}
                          />
                        )}
                      </ContactInfoCard>
                      <AffiliationsCard
                        organizations={organizations}
                        households={householdsHook.households}
                        teamId={team.id}
                        labels={labels}
                        onViewHouseholds={() => selectTab('households')}
                        onAddOrganization={openOrganizationModal}
                        onAddHousehold={householdsHook.openCreateModal}
                      />
                      <IntroducedByCard
                        key={currentContact.id}
                        team={team}
                        contactId={currentContact.id}
                        csrfToken={csrfToken}
                        introducedBy={introducedBy}
                        introducedContacts={introducedContacts}
                        canEdit={canAssignStaff}
                      />
                      <SocialCard
                        urls={{
                          facebook_url: facebookUrl,
                          instagram_url: instagramUrl,
                          twitter_url: twitterUrl,
                          linkedin_url: linkedinUrl,
                        }}
                        editingField={editingSocialField}
                        hoverField={hoverSocialField}
                        editValues={{
                          facebook_url: editFacebookUrl,
                          instagram_url: editInstagramUrl,
                          twitter_url: editTwitterUrl,
                          linkedin_url: editLinkedinUrl,
                        }}
                        onEditValueChange={(field, value) => {
                          if (field === 'facebook_url') setEditFacebookUrl(value);
                          if (field === 'instagram_url') setEditInstagramUrl(value);
                          if (field === 'twitter_url') setEditTwitterUrl(value);
                          if (field === 'linkedin_url') setEditLinkedinUrl(value);
                        }}
                        onHover={setHoverSocialField}
                        onStartEdit={startEditingSocial}
                        onSave={handleSaveSocial}
                        onCancelEdit={() => setEditingSocialField(null)}
                        submitting={socialSubmitting}
                        error={socialError}
                      />
                      <VenuePurchasesCard venues={currentContact.venues || []} />
                      <SubscriptionsCard fanPermissions={fanPermissions} onEdit={() => selectTab('permissions')} />
                      <TagsCard
                        taggedItems={taggedItems}
                        availableTags={availableTags}
                        currentUser={currentUser}
                        showTagPopup={showTagPopup}
                        setShowTagPopup={setShowTagPopup}
                        tagSearchValue={tagSearchValue}
                        setTagSearchValue={setTagSearchValue}
                        onToggleTag={handleToggleTag}
                        onCreateAndAddTag={handleCreateAndAddTag}
                      />
                      <div className="cdp-rail-bottom-spacer">
                        <FilesCard
                          attachments={attachments}
                          loading={attachmentsLoading}
                          error={attachmentsError}
                          onUpload={handleUploadAttachment}
                          onDelete={handleDeleteAttachment}
                          canManage
                        />
                      </div>
                    </div>
                  </div>
              {currentContact.geo_latitude && (
              <div style={{ background: 'var(--sl-bg-primary-alt)', margin: '0 -24px -24px', padding: '16px 24px 24px', borderRadius: '0 0 12px 12px' }}>
                <div className="row">
                    <div className="col-12 mb-4">
                      <div
                        className="card h-100"
                        style={{
                          borderRadius: 12,
                          border: '1px solid var(--sl-border-secondary)',
                        }}
                      >
                        <div className="card-body d-flex flex-column p-0">
                          <div
                            style={{
                              padding: '20px 24px 16px',
                              borderBottom: '1px solid var(--sl-border-secondary)',
                            }}
                          >
                            <h2
                              className="mb-0"
                              style={{ fontSize: 18, fontWeight: 600, color: 'var(--sl-text-primary)' }}
                            >
                              Location
                            </h2>
                            {(currentContact.geo_city || currentContact.geo_state) && (
                              <div
                                className="mt-1"
                                style={{ fontSize: 14, color: 'var(--sl-text-quaternary)' }}
                              >
                                {[currentContact.geo_city, currentContact.geo_state]
                                  .filter(Boolean)
                                  .join(', ')}
                              </div>
                            )}
                          </div>
                          <div style={{ flex: 1, minHeight: 240 }}>
                            <MapWithMarker
                              lat={currentContact.geo_latitude}
                              lon={currentContact.geo_longitude}
                              height="100%"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                </div>
              </div>
              )}
                </>
              )}

              {activeTab === 'activity' && attributionTimeline && (
                <div className="py-4">
                  <ActivityTab timeline={attributionTimeline} />
                </div>
              )}

              {activeTab === 'transactions' && (
                <TransactionsTab
                  team={team}
                  transactions={transactionsHook.transactions}
                  loading={transactionsHook.loading}
                  error={transactionsHook.error}
                  count={transactionsHook.count}
                  lastDate={transactionsHook.lastDate}
                  range={transactionsHook.range}
                  setRange={transactionsHook.setRange}
                  page={transactionsHook.page}
                  setPage={transactionsHook.setPage}
                  perPage={transactionsHook.perPage}
                  totalEntries={transactionsHook.totalEntries}
                  totalPages={transactionsHook.totalPages}
                />
              )}

              {activeTab === 'donations' && (
                <DonationsTab
                  team={team}
                  donationsHook={donationsHook}
                  canLogDonations={canLogDonations}
                  onLogDonation={() => setShowLogDonationModal(true)}
                  statementHref={(year) => `/teams/${team.id}/crm/fans/${currentContact.id}/donations/statement.pdf?year=${year}`}
                  showSoftCredits={softCreditsEnabled}
                  onAddSoftCredit={(donation) => setSoftCreditsModalDonation(donation)}
                  onEditFairMarketValue={(donation) => setFairMarketValueDonation(donation)}
                  afterDonations={softCreditsEnabled && (
                    <SoftCreditsReceivedCard softCreditsHook={softCreditsHook} subjectLabel="this contact" />
                  )}
                />
              )}

              {activeTab === 'pledges' && (
                <PledgesTab team={team} fan={contact} canManage={currentUser.can_manage_pledges} />
              )}

              {activeTab === 'organizations' && (
                <div className="mt-3">
                  <div className="card">
                    <div className="card-body p-0">
                      <div className="border-bottom px-4 py-3 d-flex justify-content-between align-items-center">
                        <h2 className="h6 mb-0">{labels.organization.plural}</h2>
                        <div className="d-flex align-items-center">
                          <button
                            type="button"
                            className="btn btn-setlist-primary btn-sm d-inline-flex align-items-center"
                            onClick={openOrganizationModal}
                          >
                            <span className="me-1">+</span>
                            <span>{`Add ${orgSingularLower}`}</span>
                          </button>
                        </div>
                      </div>

                      <Table
                        columns={[
                          {
                            key: 'organization',
                            header: labels.organization.singular,
                            primary: true,
                            width: '25%',
                            render: (organization) => (
                              <Button as="a" href={`/teams/${team.id}/crm/organizations/${organization.id}`} variant="link-color">
                                {organization.name || '(No name)'}
                              </Button>
                            ),
                          },
                          {
                            key: 'location',
                            header: 'Location',
                            width: '25%',
                            render: (organization) => (
                              [organization.city, organization.state, organization.country].filter(Boolean).join(', ') || '—'
                            ),
                          },
                          {
                            key: 'website',
                            header: 'Website',
                            width: '25%',
                            render: (organization) => (organization.website_url ? (
                              <Button
                                as="a"
                                href={organization.website_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="link-color"
                              >
                                {organization.website_url}
                              </Button>
                            ) : (
                              organization.email || '—'
                            )),
                          },
                          {
                            key: 'actions',
                            header: 'Actions',
                            align: 'right',
                            width: '25%',
                            render: (organization) => (!domainOnlyOrgIds.has(organization.id) && (
                              <Button
                                variant="link-destructive"
                                size="sm"
                                icon={Trash01}
                                onClick={(event) => handleDetachOrganization(event, organization)}
                                aria-label={`Detach ${orgSingularLower} from contact`}
                              />
                            )),
                          },
                        ]}
                        data={organizations}
                        getRowKey={(organization) => organization.id}
                        empty={<EmptyState size="sm" title={`No ${orgPluralLower} yet.`} />}
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'households' && (
                <HouseholdsTab team={team} householdsHook={householdsHook} relationshipOptions={relationshipOptions} />
              )}

              {activeTab === 'memberships' && (
                <MembershipsTab team={team} {...membershipsHook} />
              )}

              {activeTab === 'volunteers' && team.volunteers_enabled !== false && (
                <div className="py-4 text-muted">
                  Coming Soon!
                </div>
              )}

              {activeTab === 'permissions' && (
                <div className="py-4">
                  {fanPermissions && fanPermissions.fan ? (
                    <MailkickSubscriptionIndexContainer {...fanPermissions} />
                  ) : (
                    <div className="text-muted">
                      No permissions data available for this contact.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

      {showOrganizationModal && (
        <div className="team-modal-overlay">
          <div className="team-modal" style={{ maxWidth: 720 }}>
            <div className="team-modal-header">
              <div className="d-flex justify-content-between align-items-start w-100">
                <div className="flex-grow-1">
                  <h3 className="team-modal-title mb-2">{`Add ${orgSingularLower}`}</h3>
                  <CrmDualModeTabs
                    activeMode={organizationModalTab}
                    onChange={setOrganizationModalTab}
                    newLabel="Create new"
                    existingLabel="Add existing"
                  />
                </div>
                <button
                  type="button"
                  className="team-modal-close"
                  onClick={() => {
                    if (addOrgSubmitting || attachOrgSubmitting) return;
                    setShowOrganizationModal(false);
                  }}
                  aria-label="Close"
                >
                  <i className="fa fa-times" />
                </button>
              </div>
            </div>

            {organizationModalTab === 'new' && (
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (!team || !team.id || !csrfToken) return;
                  if (!addOrgName.trim()) {
                    setAddOrgErrors([`${labels.organization.singular} name is required`]);
                    return;
                  }
                  try {
                    setAddOrgSubmitting(true);
                    setAddOrgErrors([]);
                    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
                    const payload = {
                      organization: {
                        name: addOrgName.trim(),
                        city: addOrgCity.trim() || null,
                        state: addOrgState.trim() || null,
                        zip: addOrgZip.trim() || null,
                        country: addOrgCountry.trim() || null,
                        email: addOrgEmail.trim() || null,
                        phone: addOrgPhone.trim() || null,
                        website_url: addOrgWebsiteUrl.trim() || null,
                        facebook_url: addOrgFacebookUrl.trim() || null,
                        instagram_url: addOrgInstagramUrl.trim() || null,
                        twitter_url: addOrgTwitterUrl.trim() || null,
                        linkedin_url: addOrgLinkedinUrl.trim() || null,
                      },
                    };
                    const response = await axios.post(
                      `/teams/${team.id}/crm/organizations.json`,
                      payload,
                    );
                    const created = response.data;
                    if (currentContact && currentContact.id) {
                      const contactResponse = await axios.patch(
                        `/teams/${team.id}/crm/fans/${currentContact.id}.json`,
                        {
                          fan: {
                            company_name: created.name,
                          },
                        },
                      );
                      const updatedContact = contactResponse.data;
                      setCurrentContact((prev) => ({ ...prev, ...updatedContact }));
                    }
                    setOrganizations([created]);
                    setShowOrganizationModal(false);
                  } catch (err) {
                    const resp = err.response;
                    if (resp && Array.isArray(resp.data)) {
                      setAddOrgErrors(resp.data);
                    } else {
                      setAddOrgErrors([`Failed to create ${orgSingularLower}`]);
                    }
                  } finally {
                    setAddOrgSubmitting(false);
                  }
                }}
              >
                <div className="team-modal-body">
                  {addOrgErrors.length > 0 && (
                    <div className="alert alert-danger py-2">
                      <ul className="mb-0">
                        {addOrgErrors.map((err) => (
                          <li key={err}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">{`${labels.organization.singular} name`}</label>
                      <input
                        type="text"
                        className="form-control"
                        value={addOrgName}
                        onChange={(e) => setAddOrgName(e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Website</label>
                      <input
                        type="url"
                        className="form-control"
                        value={addOrgWebsiteUrl}
                        onChange={(e) => setAddOrgWebsiteUrl(e.target.value)}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">City</label>
                      <input
                        type="text"
                        className="form-control"
                        value={addOrgCity}
                        onChange={(e) => setAddOrgCity(e.target.value)}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">State</label>
                      <input
                        type="text"
                        className="form-control"
                        value={addOrgState}
                        onChange={(e) => setAddOrgState(e.target.value)}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">ZIP</label>
                      <input
                        type="text"
                        className="form-control"
                        value={addOrgZip}
                        onChange={(e) => setAddOrgZip(e.target.value)}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Country</label>
                      <input
                        type="text"
                        className="form-control"
                        value={addOrgCountry}
                        onChange={(e) => setAddOrgCountry(e.target.value)}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Email</label>
                      <input
                        type="email"
                        className="form-control"
                        value={addOrgEmail}
                        onChange={(e) => setAddOrgEmail(e.target.value)}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Phone</label>
                      <input
                        type="tel"
                        className="form-control"
                        value={addOrgPhone}
                        onChange={(e) => setAddOrgPhone(e.target.value)}
                      />
                    </div>
                    <div className="col-12 mt-2">
                      <label className="form-label">Social profiles</label>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small text-muted">Facebook URL</label>
                      <input
                        type="url"
                        className="form-control"
                        value={addOrgFacebookUrl}
                        onChange={(e) => setAddOrgFacebookUrl(e.target.value)}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small text-muted">Instagram URL</label>
                      <input
                        type="url"
                        className="form-control"
                        value={addOrgInstagramUrl}
                        onChange={(e) => setAddOrgInstagramUrl(e.target.value)}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small text-muted">X (Twitter) URL</label>
                      <input
                        type="url"
                        className="form-control"
                        value={addOrgTwitterUrl}
                        onChange={(e) => setAddOrgTwitterUrl(e.target.value)}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small text-muted">LinkedIn URL</label>
                      <input
                        type="url"
                        className="form-control"
                        value={addOrgLinkedinUrl}
                        onChange={(e) => setAddOrgLinkedinUrl(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <div className="team-modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      if (addOrgSubmitting) return;
                      setShowOrganizationModal(false);
                    }}
                    disabled={addOrgSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={!canSubmitCreateOrgForm || addOrgSubmitting}
                  >
                    {addOrgSubmitting ? 'Saving…' : `Create ${orgSingularLower}`}
                  </button>
                </div>
              </form>
            )}

            {organizationModalTab === 'existing' && (
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (!team || !team.id || !csrfToken || !currentContact || !currentContact.id || !attachOrgSelectedId) {
                    return;
                  }
                  const selected = attachOrgResults.find((org) => org.id === attachOrgSelectedId);
                  if (!selected) return;
                  try {
                    setAttachOrgSubmitting(true);
                    setAttachOrgError('');
                    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
                    const contactResponse = await axios.patch(
                      `/teams/${team.id}/crm/fans/${currentContact.id}.json`,
                      {
                        fan: {
                          company_name: selected.name,
                        },
                      },
                    );
                    const updatedContact = contactResponse.data;
                    setCurrentContact((prev) => ({ ...prev, ...updatedContact }));
                    setOrganizations([selected]);
                    setShowOrganizationModal(false);
                  } catch (err) {
                    const resp = err.response;
                    if (resp && Array.isArray(resp.data)) {
                      setAttachOrgError(resp.data.join(', '));
                    } else {
                      setAttachOrgError(`Failed to attach ${orgSingularLower}`);
                    }
                  } finally {
                    setAttachOrgSubmitting(false);
                  }
                }}
              >
                <div className="team-modal-body">
                  {attachOrgError && (
                    <div className="alert alert-danger py-2 mb-2">{attachOrgError}</div>
                  )}

                  <p className="text-muted small">
                    {`Search for an existing ${orgSingularLower} to associate with this contact. `}
                    {`Attaching will update the contact's company name to match the ${orgSingularLower}'s name.`}
                  </p>

                  <div className="mb-3 d-flex gap-2">
                    <input
                      type="search"
                      className="form-control"
                      placeholder={`Search ${orgPluralLower} by name`}
                      value={attachOrgSearch}
                      onChange={(e) => setAttachOrgSearch(e.target.value)}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={async () => {
                        if (!team || !team.id) return;
                        const query = attachOrgSearch.trim();
                        if (!query) {
                          setAttachOrgResults([]);
                          setAttachOrgSelectedId(null);
                          setAttachOrgError('');
                          return;
                        }
                        try {
                          setAttachOrgLoading(true);
                          setAttachOrgError('');
                          const response = await axios.get(`/teams/${team.id}/crm/organizations.json`, {
                            params: {
                              page: 1,
                              per_page: 25,
                              search: query,
                            },
                          });
                          const data = response.data || {};
                          const results = data.organizations || [];
                          setAttachOrgResults(results);
                          if (!results.find((org) => org.id === attachOrgSelectedId)) {
                            setAttachOrgSelectedId(null);
                          }
                        } catch (err) {
                          setAttachOrgError(`Failed to search ${orgPluralLower}`);
                        } finally {
                          setAttachOrgLoading(false);
                        }
                      }}
                      disabled={attachOrgLoading}
                    >
                      {attachOrgLoading ? 'Searching…' : 'Search'}
                    </button>
                  </div>

                  <div className="table-responsive" style={{ maxHeight: 260, overflowY: 'auto' }}>
                    <table className="table table-sm align-middle mb-0">
                      <thead>
                        <tr>
                          <th style={{ width: '1%' }}>Select</th>
                          <th>{labels.organization.singular}</th>
                          <th>Location</th>
                        </tr>
                      </thead>
                      <tbody>
                        {!attachOrgLoading && attachOrgResults.length === 0 && (
                          <tr>
                            <td colSpan="3" className="text-muted text-center py-3">
                              {attachOrgSearch.trim()
                                ? `No matching ${orgPluralLower} found.`
                                : `Enter a search term and click Search to find ${orgPluralLower}.`}
                            </td>
                          </tr>
                        )}
                        {attachOrgResults.map((org) => {
                          const loc = [org.city, org.state, org.country].filter(Boolean).join(', ');
                          return (
                            <tr key={org.id}>
                              <td>
                                <input
                                  type="radio"
                                  className="team-checkbox"
                                  checked={attachOrgSelectedId === org.id}
                                  onChange={() => setAttachOrgSelectedId(org.id)}
                                  aria-label={`Select ${org.name}`}
                                />
                              </td>
                              <td>{org.name}</td>
                              <td className="text-muted small">{loc || '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="team-modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      if (attachOrgSubmitting) return;
                      setShowOrganizationModal(false);
                    }}
                    disabled={attachOrgSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={attachOrgSubmitting || !attachOrgSelectedId}
                  >
                    {attachOrgSubmitting ? 'Attaching…' : `Attach ${orgSingularLower}`}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      <ContactFormModal
        isOpen={formModalOpen}
        onClose={() => setFormModalOpen(false)}
        contact={currentContact}
        team={team}
        csrfToken={csrfToken}
        contactTypes={contactTypes}
        customFieldDefinitions={customFieldDefinitions}
        onSaved={handleContactSaved}
      />

      {showLogDonationModal && (
        <LogDonationModal
          team={team}
          contact={currentContact}
          csrfToken={csrfToken}
          customFieldDefinitions={donationCustomFieldDefinitions}
          onClose={() => setShowLogDonationModal(false)}
          onRecorded={() => {
            donationsHook.reload();
            toast.success('Donation logged');
          }}
        />
      )}

      {fairMarketValueDonation && (
        <FairMarketValueModal
          donation={fairMarketValueDonation}
          path={`/teams/${team.id}/crm/fans/${currentContact.id}/donations/${fairMarketValueDonation.id}`}
          csrfToken={csrfToken}
          onClose={() => setFairMarketValueDonation(null)}
          onSaved={() => {
            donationsHook.reload();
            toast.success('Benefits saved');
          }}
        />
      )}

      {softCreditsModalDonation && (
        <SoftCreditsModal
          team={team}
          basePath={`/teams/${team.id}/crm/fans/${currentContact.id}/donations/${softCreditsModalDonation.id}/soft_credits`}
          omitFanIds={[currentContact.id]}
          csrfToken={csrfToken}
          onClose={() => setSoftCreditsModalDonation(null)}
          onChanged={handleSoftCreditsChanged}
        />
      )}

      {editingNote && (
        <div className="team-modal-overlay">
          <div className="team-modal" style={{ maxWidth: 720 }}>
            <div className="team-modal-header">
              <h3 className="team-modal-title">
                {editingNote.id ? 'Edit note' : 'New note'}
              </h3>
              <button
                type="button"
                className="team-modal-close"
                onClick={closeNoteModal}
                aria-label="Close"
              >
                <i className="fa fa-times" />
              </button>
            </div>
            <form onSubmit={handleSaveNote}>
              <div className="team-modal-body">
                {noteErrors.length > 0 && (
                  <div className="alert alert-danger py-2">
                    <ul className="mb-0">
                      {noteErrors.map((err) => (
                        <li key={err}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="mb-3">
                  <label className="form-label">Title</label>
                  <input
                    type="text"
                    className="form-control"
                    value={noteTitle}
                    onChange={(e) => setNoteTitle(e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Body</label>
                  <textarea
                    className="form-control"
                    rows={6}
                    value={noteBody}
                    onChange={(e) => setNoteBody(e.target.value)}
                  />
                </div>
              </div>
              <div className="team-modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={closeNoteModal}
                  disabled={noteSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={!canSubmitNoteForm || noteSubmitting}
                >
                  {noteSubmitting ? 'Saving…' : 'Save note'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {noteToDelete && (
        <div className="team-modal-overlay">
          <div className="team-modal" style={{ maxWidth: 400 }}>
            <div className="team-modal-header">
              <h3 className="team-modal-title">Delete note</h3>
              <button
                type="button"
                className="team-modal-close"
                onClick={closeDeleteNoteModal}
                aria-label="Close"
              >
                <i className="fa fa-times" />
              </button>
            </div>
            <div className="team-modal-body">
              <p style={{ margin: 0, color: 'var(--sl-text-tertiary)' }}>
                Are you sure you want to delete this note? This action cannot be undone.
              </p>
            </div>
            <div className="team-modal-footer">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={closeDeleteNoteModal}
                disabled={deleteNoteSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                style={{ backgroundColor: 'var(--sl-text-error)', borderColor: 'var(--sl-text-error)' }}
                onClick={confirmDeleteNote}
                disabled={deleteNoteSubmitting}
              >
                {deleteNoteSubmitting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Popup
        open={householdsHook.showCreateModal}
        onClose={householdsHook.closeCreateModal}
        contentStyle={{ border: 'none', borderRadius: '12px', boxShadow: '0 20px 24px -4px rgba(10,13,18,0.08)', padding: '0px', width: '480px', maxWidth: '90vw' }}
        overlayStyle={{ zIndex: '4000', backgroundColor: 'rgba(10, 13, 18, 0.7)' }}
        closeOnDocumentClick={false}
        closeOnEscape
      >
        {() => (
          <div className="team-modal" style={{ maxWidth: 480 }}>
            <div className="team-modal-header">
              <h3 className="team-modal-title">Create household</h3>
              <button
                type="button"
                className="team-modal-close"
                onClick={householdsHook.closeCreateModal}
                aria-label="Close"
              >
                <i className="fa fa-times" />
              </button>
            </div>
            <form onSubmit={householdsHook.handleCreateHousehold}>
              <div className="team-modal-body">
                {householdsHook.createErrors.length > 0 && (
                  <div className="alert alert-danger py-2">
                    <ul className="mb-0">
                      {householdsHook.createErrors.map((err) => (
                        <li key={err}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="mb-3">
                  <label className="form-label">Household name</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder={currentContact.last_name ? `${currentContact.last_name} Household` : 'e.g. Smith Household'}
                    value={householdsHook.createName}
                    onChange={(e) => householdsHook.setCreateName(e.target.value)}
                  />
                  <div className="form-text">
                    {currentContact.last_name
                      ? `Defaults to "${currentContact.last_name} Household" if left blank.`
                      : 'Leave blank to auto-name based on contact.'}
                  </div>
                </div>

                <p className="text-muted small mb-0">
                  This contact will be added as the primary member of the new household.
                </p>
              </div>
              <div className="team-modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={householdsHook.closeCreateModal}
                  disabled={householdsHook.createSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={!householdsHook.canSubmitCreate || householdsHook.createSubmitting}
                >
                  {householdsHook.createSubmitting ? 'Creating…' : 'Create household'}
                </button>
              </div>
            </form>
          </div>
        )}
      </Popup>

      <Popup
        open={householdsHook.showAddMemberModal}
        onClose={householdsHook.closeAddMemberModal}
        contentStyle={{ border: 'none', borderRadius: '12px', boxShadow: '0 20px 24px -4px rgba(10,13,18,0.08)', padding: '0px', width: '560px', maxWidth: '90vw' }}
        overlayStyle={{ zIndex: '4000', backgroundColor: 'rgba(10, 13, 18, 0.7)' }}
        closeOnDocumentClick={false}
        closeOnEscape
      >
        {() => (
          <div className="team-modal" style={{ maxWidth: 560 }}>
            <div className="team-modal-header">
              <h3 className="team-modal-title">
                Add member to {householdsHook.addMemberHousehold?.name || 'household'}
              </h3>
              <button
                type="button"
                className="team-modal-close"
                onClick={householdsHook.closeAddMemberModal}
                aria-label="Close"
              >
                <i className="fa fa-times" />
              </button>
            </div>
            <form onSubmit={householdsHook.handleAddMember}>
              <div className="team-modal-body">
                {householdsHook.addMemberError && (
                  <div className="alert alert-danger py-2 mb-2">
                    {householdsHook.addMemberError}
                  </div>
                )}

                <p className="text-muted small">
                  Search for a contact to add as a secondary member of this household.
                </p>

                <div className="mb-3">
                  <DebounceInput
                    type="search"
                    minLength={1}
                    debounceTimeout={300}
                    className="form-control"
                    placeholder="Search contacts by name"
                    value={householdsHook.memberSearch}
                    onChange={(e) => householdsHook.setMemberSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') e.preventDefault();
                    }}
                  />
                </div>

                <div className="table-responsive" style={{ maxHeight: 260, overflowY: 'auto' }}>
                  <table className="table table-sm align-middle mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: '1%' }}>Select</th>
                        <th>Contact</th>
                        <th>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!householdsHook.memberSearchLoading
                        && householdsHook.memberResults.length === 0 && (
                        <tr>
                          <td colSpan="3" className="text-muted text-center py-3">
                            {householdsHook.memberSearch.trim()
                              ? 'No matching contacts found.'
                              : 'Start typing to find contacts.'}
                          </td>
                        </tr>
                      )}
                      {householdsHook.memberResults.map((fan) => (
                        <tr key={fan.id}>
                          <td>
                            <input
                              type="radio"
                              className="team-checkbox"
                              checked={householdsHook.selectedFanId === fan.id}
                              onChange={() => householdsHook.setSelectedFanId(fan.id)}
                              aria-label={`Select ${fan.name}`}
                            />
                          </td>
                          <td>{fan.name}</td>
                          <td className="text-muted small">
                            {fan.title_email_preview || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="team-modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={householdsHook.closeAddMemberModal}
                  disabled={householdsHook.addMemberSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={householdsHook.addMemberSubmitting || !householdsHook.selectedFanId}
                >
                  {householdsHook.addMemberSubmitting ? 'Adding…' : 'Add member'}
                </button>
              </div>
            </form>
          </div>
        )}
      </Popup>

      <Popup
        open={!!householdsHook.memberToRemove}
        onClose={householdsHook.closeRemoveMemberModal}
        contentStyle={{ border: 'none', borderRadius: '12px', boxShadow: '0 20px 24px -4px rgba(10,13,18,0.08)', padding: '0px', width: '400px', maxWidth: '90vw' }}
        overlayStyle={{ zIndex: '4000', backgroundColor: 'rgba(10, 13, 18, 0.7)' }}
        closeOnDocumentClick={false}
        closeOnEscape
      >
        {() => (
          <div className="team-modal" style={{ maxWidth: 400 }}>
            <div className="team-modal-header">
              <h3 className="team-modal-title">Remove member</h3>
              <button
                type="button"
                className="team-modal-close"
                onClick={householdsHook.closeRemoveMemberModal}
                aria-label="Close"
              >
                <i className="fa fa-times" />
              </button>
            </div>
            <div className="team-modal-body">
              <p style={{ margin: 0, color: 'var(--sl-text-tertiary)' }}>
                Are you sure you want to remove this member from the household? This action cannot be undone.
              </p>
            </div>
            <div className="team-modal-footer">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={householdsHook.closeRemoveMemberModal}
                disabled={householdsHook.removeMemberSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                style={{ backgroundColor: 'var(--sl-text-error)', borderColor: 'var(--sl-text-error)' }}
                onClick={householdsHook.confirmRemoveMember}
                disabled={householdsHook.removeMemberSubmitting}
              >
                {householdsHook.removeMemberSubmitting ? 'Removing…' : 'Remove'}
              </button>
            </div>
          </div>
        )}
      </Popup>

      <Popup
        open={!!householdsHook.householdToDelete}
        onClose={householdsHook.closeDeleteHouseholdModal}
        contentStyle={{ border: 'none', borderRadius: '12px', boxShadow: '0 20px 24px -4px rgba(10,13,18,0.08)', padding: '0px', width: '400px', maxWidth: '90vw' }}
        overlayStyle={{ zIndex: '4000', backgroundColor: 'rgba(10, 13, 18, 0.7)' }}
        closeOnDocumentClick={false}
        closeOnEscape
      >
        {() => (
          <div className="team-modal" style={{ maxWidth: 400 }}>
            <div className="team-modal-header">
              <h3 className="team-modal-title">Delete household</h3>
              <button
                type="button"
                className="team-modal-close"
                onClick={householdsHook.closeDeleteHouseholdModal}
                aria-label="Close"
              >
                <i className="fa fa-times" />
              </button>
            </div>
            <div className="team-modal-body">
              <p style={{ margin: 0, color: 'var(--sl-text-tertiary)' }}>
                Are you sure you want to delete this household? All memberships will be removed. This action cannot be undone.
              </p>
            </div>
            <div className="team-modal-footer">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={householdsHook.closeDeleteHouseholdModal}
                disabled={householdsHook.deleteHouseholdSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                style={{ backgroundColor: 'var(--sl-text-error)', borderColor: 'var(--sl-text-error)' }}
                onClick={householdsHook.confirmDeleteHousehold}
                disabled={householdsHook.deleteHouseholdSubmitting}
              >
                {householdsHook.deleteHouseholdSubmitting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        )}
      </Popup>
    </CrmPageLayout>
  );
}

ContactDetailsPage.propTypes = {
  team: PropTypes.object,
  contact: PropTypes.object,
  csrfToken: PropTypes.string,
  contactTypes: PropTypes.array,
  customFieldDefinitions: PropTypes.array,
  currentUser: PropTypes.object,
  fanPermissions: PropTypes.object,
  canLogDonations: PropTypes.bool,
  relationshipOptions: PropTypes.array,
  staffOptions: PropTypes.array,
  assignedStaff: PropTypes.object,
  canAssignStaff: PropTypes.bool,
  introducedBy: PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }),
  introducedContacts: PropTypes.shape({ count: PropTypes.number, contacts: PropTypes.array }),
  attributionTimeline: PropTypes.object,
};

ContactDetailsPage.defaultProps = {
  team: null,
  contact: null,
  csrfToken: null,
  contactTypes: [],
  customFieldDefinitions: [],
  currentUser: null,
  fanPermissions: null,
  canLogDonations: false,
  relationshipOptions: [],
  staffOptions: null,
  assignedStaff: null,
  canAssignStaff: false,
  introducedBy: null,
  introducedContacts: undefined,
  attributionTimeline: null,
};

export default ContactDetailsPage;